/**
 * Walk a JS value (object/array/primitive) and collect every string that looks
 * like an entity id (`<table>_<rest>`) whose table prefix is in `knownTables`.
 *
 * Used by the sync runtime to extract referenced ids (FK columns and ids
 * nested inside JSON columns like `contactPersons[].id`) from message values.
 * The recursion is depth-bounded and cycle-guarded to keep cost predictable
 * even on hostile or self-referential payloads.
 */

const ENTITY_ID_PATTERN = /^([a-zA-Z][a-zA-Z0-9]*)_.+/

export function walkEntityIds(
  value: unknown,
  knownTables: ReadonlySet<string>,
  out: Set<string>,
  depth = 6,
  visited: Set<unknown> = new Set(),
): void {
  if (value == null || depth <= 0) return
  if (typeof value === 'string') {
    const m = ENTITY_ID_PATTERN.exec(value)
    if (m && knownTables.has(m[1] as string)) out.add(value)
    return
  }
  if (typeof value !== 'object') return
  if (visited.has(value)) return
  visited.add(value)

  if (Array.isArray(value)) {
    for (const item of value) walkEntityIds(item, knownTables, out, depth - 1, visited)
    return
  }
  for (const v of Object.values(value)) walkEntityIds(v, knownTables, out, depth - 1, visited)
}
